import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Button from "./Button";
import { useResponsiveMobile } from "../hooks/useResponsiveMobile";

type DropdownItem = {
  label: string;
  to: string;
  icon: string;
  hoverIcon: string;
};

const Dropdown = ({ items }: { items: DropdownItem[] }) => {
  return (
    <div
      className="
        absolute left-1/2 top-14 -translate-x-1/2
       hidden group-hover:block
        z-50
      "
    >
      <div className="w-[172px] rounded-xl bg-white shadow-lg ring-1 ring-black/5 p-2">
        {items.map((it) => (
          <Link
            key={it.label}
            to={it.to}
            className="
              group/item 
              flex items-center gap-1
              rounded-lg py-2
              text-sm font-semibold text-[#1A1A1A]
              hover:bg-gray-100
              outline-none
    
            "
          >
            <span className="relative h-6 w-6 shrink-0">
              <img
                src={it.icon}
                alt=""
                className="absolute top-[-1px] inset-0 h-6 w-6 opacity-100 transition-opacity duration-150 group-hover/item:opacity-0"
              />
              <img
                src={it.hoverIcon}
                alt=""
                className="absolute top-[-1px]  inset-0 h-6 w-6 opacity-0 transition-opacity duration-150 group-hover/item:opacity-100"
              />
            </span>

            <span className="transition-colors text-base duration-150 group-hover/item:text-[#0079FF] self-end">
              {it.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const Header = ({
  className = "",
  onClickHamburger,
}: {
  className?: string;
  onClickHamburger?: () => void;
}) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const isHome = pathname === "/";
  const isApplication = pathname === "/inquiry-form";

  //  Header 전용 반응형 값
  const isMobileView = useResponsiveMobile(900);

  //  스크롤 기준 다크 헤더 여부
  const [isScrolledDark, setIsScrolledDark] = useState(false);

  useEffect(() => {
    if (!isHome) return;

    const onScroll = () => {
      if (isHome) {
        //  기존 isMobile() 대신 isMobileView 사용
        setIsScrolledDark(window.scrollY >= (isMobileView ? 50 : 160));
      } else if (isApplication) {
        setIsScrolledDark(window.scrollY >= 70);
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [isHome, isApplication, isMobileView]); //  isMobileView 의존성 추가

  const isDarkHeader =
    pathname.startsWith("/solution") ||
    pathname.startsWith("/features") ||
    pathname.startsWith("/efficiency") ||
    pathname.startsWith("/case-studies") ||
    pathname.startsWith("/help-center") ||
    pathname.startsWith("/faq") ||
    (pathname.startsWith("/inquiry-form") && isScrolledDark) ||
    (isHome && isScrolledDark);

  const textColor = isDarkHeader ? "text-[#000]" : "text-[#FFF]";

  const bgColor = isDarkHeader ? "bg-white shadow-sm" : "bg-transparent";
  const parentBtnClass = `px-8 py-[26px] text-lg font-bold ${textColor} w-full whitespace-nowrap`;

  const introductionFucntion: DropdownItem[] = [
    {
      label: "결제/정산 내역",
      to: "/features#settlement-history",
      icon: "/assets/images/icon/header_list.svg",
      hoverIcon: "/assets/images/icon/header_hover_list.svg",
    },
    {
      label: "정산 서비스",
      to: "/features#payout-service",
      icon: "/assets/images/icon/header_settle.svg",
      hoverIcon: "/assets/images/icon/header_hover_settle.svg",
    },
    {
      label: "가맹점 서비스",
      to: "/features#merchant-mgmt",
      icon: "/assets/images/icon/header_merchant.svg",
      hoverIcon: "/assets/images/icon/header_hover_merchant.svg",
    },
    {
      label: "결제 서비스",
      to: "/features#payment-system",
      icon: "/assets/images/icon/header_payment.svg",
      hoverIcon: "/assets/images/icon/header_hover_payment.svg",
    },
    {
      label: "영업대행 관리 서비스",
      to: "/features#sales-network",
      icon: "/assets/images/icon/header_sale.svg",
      hoverIcon: "/assets/images/icon/header_hover_sale.svg",
    },
  ];

  const serviceGuideItems: DropdownItem[] = [
    {
      label: "품 소개",
      to: "/solution",
      icon: "/assets/images/icon/header_intro.svg",
      hoverIcon: "/assets/images/icon/header_hover_intro.svg",
    },
    {
      label: "도입효과",
      to: "/efficiency",
      icon: "/assets/images/icon/header_effect.svg",
      hoverIcon: "/assets/images/icon/header_hover_effect.svg",
    },
    {
      label: "협력사와 사례",
      to: "/case-studies",
      icon: "/assets/images/icon/header_case.svg",
      hoverIcon: "/assets/images/icon/header_hover_case.svg",
    },
  ];

  const applyItems: DropdownItem[] = [
    {
      label: "품 도입문의",
      to: "/inquiry-form?type=adoption",
      icon: "/assets/images/icon/header_application.svg",
      hoverIcon: "/assets/images/icon/header_hover_application.svg",
    },
    {
      label: "FAQ",
      to: "/faq",
      icon: "/assets/images/icon/header_faq.svg",
      hoverIcon: "/assets/images/icon/header_hover_faq.svg",
    },
    {
      label: "고객센터",
      to: "/help-center",
      icon: "/assets/images/icon/header_inquiry.svg",
      hoverIcon: "/assets/images/icon/header_hover_inquiry.svg",
    },
  ];

  return isMobileView ? (
    <header
      id="app-header"
      className={`flex flex-row justify-between items-center p-4 z-[999] w-full ${bgColor} ${pathname === "/inquiry-form" ? `absolute` : `fixed`}`}
    >
      <img
        src={isDarkHeader ? "/assets/images/logo/logo_black.svg" : "/assets/images/logo/logo.png"}
        alt="logo"
        className="w-[78px] h-[26px]"
        onClick={() => navigate("/")}
        style={{ cursor: "pointer" }}
      />

      <button
        type="button"
        onClick={onClickHamburger}
        className="h-12 w-12 grid place-items-center"
        aria-label="메뉴 열기"
      >
        <img
          src={
            isDarkHeader
              ? "/assets/images/icon/hamburger_black.png"
              : "/assets/images/icon/hamburger.png"
          }
          alt=""
          className="h-12 w-12"
        />
      </button>
    </header>
  ) : (
    <header
      className={`z-999 flex w-full md:px-[40px] lg:px-20 xl:px-[160px] h-20 items-center justify-between transition-colors duration-200  ${bgColor} ${className}`}
    >
      <img
        onClick={() => navigate("/")}
        src={isDarkHeader ? "/assets/images/logo/logo_black.svg" : "/assets/images/logo/logo.png"}
        alt="Logo"
        className="h-8 w-auto cursor-pointer"
      />

      <nav className="flex flex-row items-center">
        <div className="relative group">
          <button
            onClick={() => {
              navigate("/features");
            }}
            type="button"
            className={parentBtnClass + (pathname === "/features" ? " text-[#0079FF]" : "")}
          >
            기능소개
          </button>
          <Dropdown items={introductionFucntion} />
        </div>
        <div className="relative group">
          <button
            onClick={() => {
              navigate("/solution");
            }}
            type="button"
            className={
              parentBtnClass +
              (pathname === "/solution" ||
              pathname === "/efficiency" ||
              pathname === "/case-studies"
                ? " text-[#0079FF]"
                : "")
            }
          >
            서비스 안내
          </button>
          <Dropdown items={serviceGuideItems} />
        </div>

        <div className="relative group">
          <button
            type="button"
            className={parentBtnClass}
            onClick={() => {
              navigate("/inquiry-form?type=adoption");
            }}
          >
            서비스 신청하기
          </button>
          <Dropdown items={applyItems} />
        </div>

        <div
          onClick={() => window.open("https://www.wiezon.com/")}
          className={`px-8 py-[26px] text-lg font-bold ${textColor} w-full whitespace-nowrap flex-row flex gap-2 cursor-pointer`}
        >
          <span>회사소개</span>
          <img
            className="w-6 h-6"
            src={
              isDarkHeader
                ? "/assets/images/icon/introductionCor_black.svg"
                : "/assets/images/icon/introductionCor_white.svg"
            }
            alt="icon"
          />
        </div>
      </nav>

      {!(pathname === "/inquiry-form" && search === "?type=adoption") ? (
        <Button
          id="btn-gnb-apply"
          onClick={() => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "inquiry_click",
              click_location: "gnb",
              button_id: "btn-gnb-apply",
              page_path: window.location.pathname,
            });
            navigate("/inquiry-form?type=adoption");
          }}
          size="sm"
          className="shrink-0 text-[clamp(16px,1.4vw,20px)]  px-8 h-[56px] rounded-[70px] border-[3px] border-transparent
        [background-origin:border-box]
        [background-clip:padding-box,border-box]
        bg-[linear-gradient(#fff,#fff),linear-gradient(90deg,#0079FF_0%,#00FF44_56.09%,#FFC400_100%)]
        text-[#0079FF]"
        >
          품 도입 문의
        </Button>
      ) : (
        <div className="text-[20px] px-8 h-[56px] rounded-[70px] w-[160px]" />
      )}
    </header>
  );
};
